import React, { useState } from "react";
import {
  useChairmanUpgradeApplicationsQuery,
  useFinalizeUpgradeApplicationMutation,
} from "../api/committeeReviewApi";
import LoadError from "../components/LoadError";

function grade(app: any) {
  const from = app?.from_category?.name ?? app?.fromCategory?.name;
  const to = app?.to_category?.name ?? app?.toCategory?.name;
  return [from, to].filter(Boolean).join(" → ") || "Membership upgrade";
}

function DecisionPill({ decision }: { decision: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${map[decision] || map.pending}`}>
      {decision}
    </span>
  );
}

function AppCard({ item, onDone }: { item: any; onDone: () => void }) {
  const app = item.application || {};
  const reviews: any[] = item.reviews || [];
  const s = item.summary || {};
  const [remark, setRemark] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [finalize, { isLoading }] = useFinalizeUpgradeApplicationMutation();

  const act = async (decision: "approved" | "rejected") => {
    setBanner(null);
    if (decision === "rejected" && !window.confirm("Reject this upgrade? This is the committee's final decision.")) return;
    try {
      await finalize({ id: app.id, decision, remark: remark.trim() || undefined }).unwrap();
      setBanner({ ok: true, text: `Application ${decision}.` });
      onDone();
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || e?.message || "Could not finalise." });
    }
  };

  const done = ["approved", "rejected"].includes(app.status);

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div className="p-5 border-b bg-purple-50/40 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-gray-900">{app.user?.name ?? "Applicant"}</div>
          <div className="text-sm text-gray-500">{grade(app)}</div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded-full border bg-white">Reviewers: <b>{s.total ?? reviews.length}</b></span>
          <span className="px-2 py-1 rounded-full border bg-emerald-50 text-emerald-700">Approve: <b>{s.approved ?? 0}</b></span>
          <span className="px-2 py-1 rounded-full border bg-red-50 text-red-700">Reject: <b>{s.rejected ?? 0}</b></span>
          <span className="px-2 py-1 rounded-full border bg-amber-50 text-amber-700">Pending: <b>{s.pending ?? 0}</b></span>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Member recommendations */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-semibold">Committee member</th>
                <th className="py-2 font-semibold">Recommendation</th>
                <th className="py-2 font-semibold">Remark</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r: any) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 font-medium text-gray-800">{r.name}</td>
                  <td className="py-2"><DecisionPill decision={r.decision} /></td>
                  <td className="py-2 text-gray-600">{r.remark ?? "—"}</td>
                </tr>
              ))}
              {!reviews.length ? (
                <tr>
                  <td colSpan={3} className="py-4 text-gray-500">No members assigned yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {banner ? (
          <div
            className={`text-sm rounded-lg p-3 border ${
              banner.ok ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-red-50 border-red-100 text-red-700"
            }`}
          >
            {banner.text}
          </div>
        ) : null}

        {/* Chairman final review */}
        {done ? (
          <div className="text-sm text-gray-600">
            Final decision recorded: <DecisionPill decision={app.status} />
          </div>
        ) : (
          <div className="space-y-2 border-t pt-3">
            <div className="text-sm font-semibold text-gray-800">Final review</div>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={2}
              placeholder="Optional remark for your final decision"
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => act("rejected")}
                disabled={isLoading}
                className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
              >
                Reject
              </button>
              <button
                onClick={() => act("approved")}
                disabled={isLoading}
                className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                {isLoading ? "Saving…" : "Approve"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChairmanUpgradeApplicationsPage() {
  const { data: items = [], isLoading, isError, error, refetch } = useChairmanUpgradeApplicationsQuery();

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <div className="rounded-2xl bg-gradient-purple text-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold">Committee — Upgrade Applications</h1>
        <p className="text-sm text-white/80 mt-1">
          Review every committee member's recommendation and make the final decision.
        </p>
      </div>

      {isLoading ? (
        <div className="text-gray-600">Loading…</div>
      ) : isError ? (
        <LoadError error={error} onRetry={refetch} />
      ) : !items.length ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-gray-500">
          No applications are under committee review right now.
        </div>
      ) : (
        items.map((it: any) => <AppCard key={it.application?.id} item={it} onDone={refetch} />)
      )}
    </div>
  );
}
